// Info tab: port of RecordInfoTab.swift, read-only (Phase 1). The same cards in the same order (Contact, Address,
// Pipeline, Ownership, Custom fields, then the details iOS files under "Settings"), with the pickers and editors
// replaced by their values. Custom fields render by type (customFieldDisplay). The Actions card is left out: every
// action there is a write.

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { RECORD_TYPE_TITLES } from '@/contracts/enums';
import {
  recordDisplayName,
  type CustomFieldPayload,
  type LeadSourcePayload,
  type RecordPayload,
  type TerritoryPayload,
} from '@/contracts/models';
import { parseWireDate } from '@/contracts/wireDate';
import type { DataItem } from '@/data/hooks';
import { statsOf } from '@/domain/derived';
import { formatShortDate, formatShortDateTime } from '@/domain/format';
import { isValidCoordinate } from '@/domain/geo';
import { pipelineFor, stageFor } from '@/domain/pipelines';
import { lookup, userName } from '@/domain/util';
import { OwnerAvatar, PriorityChip, StageChip, storeValueText, type StoreRowLookups } from '@/features/common';
import { Chip, ColorDot, KeyValueRow, SectionCard } from '@/ui';
import { customValueOf } from './columns';
import {
  addressLines,
  customFieldDisplay,
  customFieldsFor,
  isAddressEmpty,
  mailHref,
  telHref,
  webHref,
  type CustomFieldDisplay,
} from './detailModel';

export interface StoreInfoTabProps {
  item: DataItem<RecordPayload>;
  lookups: StoreRowLookups;
  territory: TerritoryPayload | undefined;
  source: LeadSourcePayload | undefined;
  /** The account a contact belongs to (or a converted lead's account), when the viewer can see it. */
  parent: DataItem<RecordPayload> | undefined;
  customFields: readonly CustomFieldPayload[];
  /** Territory names link to their page for managers. */
  linkTerritory: boolean;
  now: Date;
}

function ExternalA({ href, children }: { href: string; children: ReactNode }) {
  const web = href.startsWith('http');
  return (
    <a
      href={href}
      className="text-link hover:underline"
      {...(web ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
    >
      {children}
    </a>
  );
}

function LinkValue({ value, href }: { value: string; href: string | null }) {
  if (value === '') return null;
  return href ? <ExternalA href={href}>{value}</ExternalA> : <>{value}</>;
}

function CustomValue({ d }: { d: CustomFieldDisplay }) {
  switch (d.kind) {
    case 'empty':
      return null;
    case 'text':
      return <>{d.text}</>;
    case 'multiline':
      return <span className="block text-left whitespace-pre-line">{d.text}</span>;
    case 'chips':
      return (
        <span className="inline-flex flex-wrap justify-end gap-1">
          {d.items.map((t) => (
            <Chip key={t} color="#102A5E">
              {t}
            </Chip>
          ))}
        </span>
      );
    case 'link':
      return <ExternalA href={d.href}>{d.text}</ExternalA>;
  }
}

export function StoreInfoTab({ item, lookups, territory, source, parent, customFields, linkTerritory, now }: StoreInfoTabProps) {
  const r = item.value;
  const lk = lookups;
  const pipeline = pipelineFor(r, lk.pipelines);
  const stage = stageFor(r, lk.pipelines);
  const owner = lookup(lk.users, r.ownerId);
  const lines = addressLines(r.address);
  const fields = customFieldsFor(customFields, r.type);
  const created = parseWireDate(r.createdAt);
  const updated = parseWireDate(item.modifiedAt);
  const stageEntered = parseWireDate(r.stageEnteredAt);
  const wonAt = parseWireDate(r.wonAt);
  const lastActivityAt = statsOf(lk.stats, r.id).lastActivityAt;
  const coordinate = isValidCoordinate(r.coordinate) ? r.coordinate : undefined;

  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <SectionCard title="Contact">
        <dl className="divide-y divide-line">
          <KeyValueRow label="Name" value={r.name} />
          <KeyValueRow label="Company" value={r.company} />
          {(r.type === 'contact' || r.jobTitle !== '') && <KeyValueRow label="Title" value={r.jobTitle} />}
          <KeyValueRow label="Type" value={RECORD_TYPE_TITLES[r.type]} />
          <KeyValueRow label="Phone" value={<LinkValue value={r.phone} href={telHref(r.phone)} />} />
          {r.altPhone !== '' && <KeyValueRow label="Alt phone" value={<LinkValue value={r.altPhone} href={telHref(r.altPhone)} />} />}
          <KeyValueRow label="Email" value={<LinkValue value={r.email} href={mailHref(r.email)} />} />
          <KeyValueRow label="Website" value={<LinkValue value={r.website} href={webHref(r.website)} />} />
        </dl>
        {r.notes.trim() !== '' && (
          <p className="mt-2 border-t border-line pt-3 text-sm whitespace-pre-line break-words text-fg-muted">{r.notes}</p>
        )}
      </SectionCard>

      <SectionCard title="Address">
        {isAddressEmpty(r.address) ? (
          <p className="text-sm text-fg-subtle">No address</p>
        ) : (
          <address className="text-sm not-italic">
            {lines.line1 !== '' && <span className="block font-medium text-fg">{lines.line1}</span>}
            {lines.line2 !== '' && <span className="block text-fg-muted">{lines.line2}</span>}
          </address>
        )}
        <p className="mt-2 text-xs text-fg-muted">
          {coordinate ? (
            <span className="font-mono">
              {coordinate.latitude.toFixed(5)}, {coordinate.longitude.toFixed(5)}
            </span>
          ) : (
            'No pin on the map yet.'
          )}
        </p>
      </SectionCard>

      <SectionCard title="Pipeline">
        <dl className="divide-y divide-line">
          <KeyValueRow label="Pipeline" value={pipeline?.name} />
          <KeyValueRow label="Stage" value={<StageChip stage={stage} filled emptyLabel="No stage" />} />
          {stage && <KeyValueRow label="Win probability" value={`${stage.probability}%`} />}
          {stage && stageEntered && <KeyValueRow label="In stage since" value={formatShortDate(stageEntered)} />}
          {stage?.isLost && <KeyValueRow label="Loss reason" value={r.lostReason} />}
          {wonAt && <KeyValueRow label="Won on" value={formatShortDate(wonAt)} />}
          <KeyValueRow label="Priority" value={<PriorityChip priority={r.priority} showTitle />} />
          <KeyValueRow label="Value" value={storeValueText(r.value, lk.currencyCode, lk.locale)} />
        </dl>
      </SectionCard>

      <SectionCard title="Ownership">
        <dl className="divide-y divide-line">
          <KeyValueRow
            label="Owner"
            value={<OwnerAvatar user={owner} presence={owner ? lookup(lk.presence, owner.id) : undefined} showName size="sm" />}
          />
          <KeyValueRow
            label="Territory"
            value={
              territory ? (
                <span className="inline-flex items-center gap-1.5">
                  <ColorDot color={territory.colorHex} size={8} />
                  {linkTerritory ? (
                    <Link to={`/territories/${territory.id.toLowerCase()}`} className="text-link hover:underline">
                      {territory.name}
                    </Link>
                  ) : (
                    territory.name
                  )}
                </span>
              ) : (
                <span className="font-normal text-fg-muted">None (auto-assigned by location)</span>
              )
            }
          />
          <KeyValueRow label="Source" value={source?.name ?? <span className="font-normal text-fg-muted">None</span>} />
          {parent && (
            <KeyValueRow
              label={r.type === 'contact' ? 'Account' : 'Parent'}
              value={
                <Link to={`/stores/${parent.id.toLowerCase()}`} className="text-link hover:underline">
                  {recordDisplayName(parent.value)}
                </Link>
              }
            />
          )}
          <KeyValueRow
            label="Tags"
            value={
              r.tags.length === 0 ? null : (
                <span className="inline-flex flex-wrap justify-end gap-1">
                  {r.tags.map((t) => (
                    <Chip key={t} color="#009E9E">
                      {t}
                    </Chip>
                  ))}
                </span>
              )
            }
          />
        </dl>
      </SectionCard>

      {fields.length > 0 && (
        <SectionCard title="Custom fields">
          <dl className="divide-y divide-line">
            {fields.map((f) => {
              const d = customFieldDisplay(f, customValueOf(r, f.id), lk.currencyCode, lk.locale);
              return <KeyValueRow key={f.id} label={f.name || 'Untitled field'} value={d.kind === 'empty' ? null : <CustomValue d={d} />} />;
            })}
          </dl>
        </SectionCard>
      )}

      <SectionCard title="Details">
        <dl className="divide-y divide-line">
          <KeyValueRow label="Do not contact" value={r.doNotContact ? <span className="text-danger-text">Yes</span> : 'No'} />
          <KeyValueRow label="External ID" value={r.externalId} />
          <KeyValueRow
            label="Created"
            value={created ? `${formatShortDateTime(created, now)} · ${userName(lk.users, r.createdBy)}` : null}
          />
          <KeyValueRow label="Last updated" value={updated ? formatShortDateTime(updated, now) : null} />
          {lastActivityAt && <KeyValueRow label="Last activity" value={formatShortDateTime(lastActivityAt, now)} />}
        </dl>
      </SectionCard>
    </div>
  );
}
